import { Delete02Icon, Flag01Icon, QrCodeIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Typography, Stack, Button, Select, MenuItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid";
import { useAppDispatch, useAppSelector } from "@/hooks";
import useConfirmation from "@/hooks/useConfirmation";
import { addTable, deleteTable, assignUserToTable, organizationById } from "../slice";
import { useAccessControl } from "@/features/access-control";
import { DataGrid } from "@/components";
import { Organization, Table } from "@/types";
import ROUTES from "@/constants/routes";
import { RoleBadge } from ".";

const Tables = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [selectedTable, setSelectedTable] = useState<Table["id"]>();

  const { organizationId } = useParams<{
    organizationId: string;
  }>();

  const organization = useAppSelector(organizationById(organizationId)) as unknown as Organization;

  const canvasRef = useRef<HTMLDivElement>();

  const currentOrganization = useAppSelector((state) => state.organizations[organizationId ?? ""]);
  const tables = useAppSelector((state) => state.organizations[organizationId ?? ""]?.tables ?? []);
  const { hasAccess } = useAccessControl();

  const [renderConfirmationDialog, confirmTableDeletion] = useConfirmation({
    title: "Eliminar",
    message: "Desea eliminar la mesa",
    dispatcher: deleteTable,
  });

  const getTableTitle = useCallback(
    (tableId: Table["id"]) => {
      const tables = organization?.tables ?? [];
      const table = tables.find(({ id }) => id === tableId);
      return table?.title ?? "";
    },
    [organization?.tables]
  );

  const handleDelete = (tableId: string) => {
    confirmTableDeletion({ organizationId, tableId });
  };

  const handleCreate = () => {
    if (organizationId) {
      dispatch(addTable(organizationId));
    }
  };

  const accessList = useMemo(() => {
    return Object.values(currentOrganization?.access ?? []);
  }, [currentOrganization?.access]);

  const handleChange = (tableId: string, userId: string) => {
    if (!organizationId || !userId || !tableId) return;

    dispatch(assignUserToTable({ organizationId, tableId, userId }));
  };

  const onClose = () => {
    setSelectedTable(undefined);
  };

  const handleDownloadQR = () => {
    const canvas = canvasRef?.current?.children[0];
    const imgFile = canvas?.toDataURL("image/png");

    const downloadLink = document.createElement("a");
    downloadLink.download = `QRCode-${getTableTitle(selectedTable ?? "")}`;
    downloadLink.href = `${imgFile}`;
    downloadLink.click();
  };

  const goToLandingPage = useCallback(
    (tableId: string) => {
      if (!organizationId) return;
      //TODO: replace with new aliasOrganization route
      navigate(ROUTES.dineInFlow.home.replace(":organizationId", organizationId).replace(":tableId", tableId));
    },
    [organizationId]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        flex: 2,
        field: "title",
        headerName: "Nombre",
        hideSortIcons: true,
        disableColumnMenu: true,
      },
      {
        flex: 2,
        field: "id",
        headerName: "Mesero",
        hideSortIcons: true,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<Table>) => {
          return (
            <Select
              value={params.row.assignedTo}
              onChange={(e) => {
                handleChange(params.row.id, e.target.value);
              }}
              renderValue={(value) => {
                if (!value) {
                  return "Elige un Mesero";
                }
                const user = accessList.find(({ id }) => id === value);
                return user ? user.name : "";
              }}
              fullWidth
              variant="outlined"
              size="small"
              displayEmpty
            >
              {accessList.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <ListItemText primary={user.name} />
                  <RoleBadge role={user.role} />
                </MenuItem>
              ))}
            </Select>
          );
        },
      },
      {
        type: "actions",
        field: "actions",
        headerName: "Acciones",
        align: "center",
        headerAlign: "center",
        hideSortIcons: true,
        disableColumnMenu: true,
        getActions: ({ id }: GridRowParams<Table>) => [
          <GridActionsCellItem icon={<HugeiconsIcon icon={Delete02Icon} />} onClick={() => handleDelete(id as unknown as string)} label="Eliminar" />,
          <GridActionsCellItem icon={<HugeiconsIcon icon={QrCodeIcon} />} onClick={() => setSelectedTable(id as unknown as string)} label="Código QR" />,
          <GridActionsCellItem icon={<HugeiconsIcon icon={Flag01Icon} />} onClick={() => goToLandingPage(id as unknown as string)} label="Perfil del comercio" />,
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessList]
  );

  return (
    <Stack spacing={3}>
      <Stack spacing={2} direction="row" alignItems="center">
        <Typography variant="h2" flex={1}>
          Mesas Disponibles
        </Typography>
        {hasAccess("create.table") && (
          <Button size="large" color="success" fullWidth={false} onClick={handleCreate}>
            Crear Mesa
          </Button>
        )}
      </Stack>
      <DataGrid rows={tables} columns={columns} />

      <Dialog open={!!selectedTable} onClose={onClose} maxWidth="xl">
        <DialogTitle> Código QR</DialogTitle>

        <DialogContent>
          <Stack
            sx={{
              width: "300px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div ref={canvasRef}>
              <QRCodeCanvas
                value={`${document.location.origin}${ROUTES.dineInFlow.home.replace(":organizationId", organizationId ?? "").replace(":tableId", selectedTable ?? "")}`}
                size={200}
                bgColor={"#ffffff"}
                fgColor={"#000000"}
                level={"L"}
                includeMargin={false}
                imageSettings={{
                  src: "https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fnew.png?alt=media&token=923cf711-2fdf-40af-b127-99ee6bd75e57",
                  x: undefined,
                  y: undefined,
                  height: 42,
                  width: 42,
                  excavate: true,
                }}
              />
            </div>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
          <Button color="success" onClick={handleDownloadQR}>
            Descargar
          </Button>
        </DialogActions>
      </Dialog>

      {renderConfirmationDialog()}
    </Stack>
  );
};

export default Tables;
